import { createApp } from '@backstage/frontend-defaults';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { navModule } from './modules/nav';
import homePlugin from '@backstage/plugin-home/alpha';
import {
  createFrontendModule,
  ApiBlueprint,
  createApiFactory,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import { PageBlueprint } from '@backstage/frontend-plugin-api';
import { compatWrapper } from '@backstage/core-compat-api';
import { HomePage } from './modules/home';
import { ProjectGuidePage } from './modules/docs';
import { CustomSettingsPage } from './modules/settings/CustomSettingsPage';
import { ThemeBlueprint, TranslationBlueprint } from '@backstage/plugin-app-react';
import { createUnifiedTheme, UnifiedThemeProvider, palettes } from '@backstage/theme';
import { BACKSTAGE_TOKEN_KEY } from './auth/authApi';
import { apiDocsTranslationRef } from '@backstage/plugin-api-docs';
import { createTranslationMessages } from '@backstage/frontend-plugin-api';

function getBackendBase() {
  return window.location.port === '3000'
    ? `${window.location.protocol}//${window.location.hostname}:7007`
    : window.location.origin;
}

// Override api:app/fetch to inject the Backstage bridge token for backend calls.
// identityApiRef cannot be overridden (it is registered as "static" by AppManager),
// so we intercept at the fetch layer instead.
const localFetchApiExtension = ApiBlueprint.makeWithOverrides({
  name: 'fetch',
  factory: originalFactory =>
    originalFactory(
      defineParams =>
        defineParams(
          createApiFactory({
            api: fetchApiRef,
            deps: {},
            factory: () => ({
              fetch: async (
                input: Parameters<typeof fetch>[0],
                init?: Parameters<typeof fetch>[1],
              ): Promise<Response> => {
                const url =
                  typeof input === 'string'
                    ? input
                    : input instanceof URL
                      ? input.href
                      : (input as Request).url;

                const backendBase = getBackendBase();
                const token = localStorage.getItem(BACKSTAGE_TOKEN_KEY);

                if (
                  token &&
                  url.startsWith(`${backendBase}/api/`) &&
                  !url.includes('/api/local-auth')
                ) {
                  const headers = new Headers(init?.headers);
                  if (!headers.has('Authorization')) {
                    headers.set('Authorization', `Bearer ${token}`);
                  }
                  return window.fetch(input, { ...init, headers });
                }

                return window.fetch(input, init);
              },
            }),
          }),
        ),
    ),
});

const fetchBridgeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [localFetchApiExtension],
});

const HomepageContent = PageBlueprint.makeWithOverrides({
  factory: originalFactory =>
    originalFactory({
      path: '/',
      loader: async () => compatWrapper(<HomePage />),
    }),
});

const homeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [HomepageContent],
});

const CustomSettingsContent = PageBlueprint.makeWithOverrides({
  factory: originalFactory =>
    originalFactory({
      path: '/settings',
      loader: async () => compatWrapper(<CustomSettingsPage />),
    }),
});

const settingsModule = createFrontendModule({
  pluginId: 'user-settings',
  extensions: [CustomSettingsContent],
});

const ProjectGuideContent = PageBlueprint.makeWithOverrides({
  name: 'project-guide-page',
  factory: originalFactory =>
    originalFactory({
      path: '/doc',
      loader: async () => compatWrapper(<ProjectGuidePage />),
    }),
});

// Note: `/docs` is intentionally NOT aliased here so the discovered
// TechDocs plugin (auto-loaded via `app.packages: all`) owns the
// `/docs` index and `/docs/:namespace/:kind/:name/*` reader routes.
const projectGuideModule = createFrontendModule({
  pluginId: 'app',
  extensions: [ProjectGuideContent],
});

const acmeTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: '#005643' },
    secondary: { main: '#622599' },
    background: { default: '#f7fbf4', paper: '#ffffff' },
    text: {
      primary: '#2c352d',
      secondary: '#586159',
    },
    navigation: {
      ...palettes.light.navigation,
      background: '#005643',
      indicator: '#f7e017',
      color: '#ffffff',
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: 'rgba(247, 224, 23, 0.15)',
      },
    },
  },
});

const acmeThemeExtension = ThemeBlueprint.make({
  params: {
    theme: {
      id: 'acme',
      title: 'Acme',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={acmeTheme}>{children}</UnifiedThemeProvider>
      ),
    },
  },
});

const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [acmeThemeExtension],
});

const apiDocsTranslationExtension = TranslationBlueprint.make({
  params: {
    resource: createTranslationMessages({
      ref: apiDocsTranslationRef,
      messages: {
        'defaultApiExplorerPage.createButtonTitle': 'Add New API',
      },
    }),
  },
});

const translationsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [apiDocsTranslationExtension],
});

export default createApp({
  features: [
    homePlugin,
    homeModule,
    catalogPlugin,
    navModule,
    themeModule,
    fetchBridgeModule,
    settingsModule,
    projectGuideModule,
    translationsModule,
  ],
});
